import { parse, differenceInMinutes } from "date-fns";
import { isTimeValid, getUTCDate} from '@dataGrid/gridUtilities';
import appConstant from "../../appConstants";

// List of possible operand options
const operandTokens = [
  "isOneOf",
  "contains",
  "greaterThan",
  "lesserThan",
  "inBetween",
  "includes",
  "lesserThanEqualTo",
  "greaterThanEqualTo",
  "dateRange",
];

// Generate quick filter string from filters that are active
function generateQuickFilterString(filterObject){
    const activeFilter = {};
    if(!filterObject || !filterObject.filterGroups){
        return appConstant.EMPTY_STRING;
    }
    const filtersList = Object.keys(filterObject.filterGroups).map(filterGroupKey => {
       const filter = filterObject.filterGroups[filterGroupKey];
       let filterString = filter.filters.map(currentFilter => {
            if(currentFilter.actionType === "switch"){
               const activeOption =  currentFilter.options.find(option => option.isApplied);
               return activeOption ? parseFilter(activeOption) : "";
            } else {
                return parseFilter(currentFilter)
            }
        })
        .filter(filter => filter)
        .join(" || ");
        // combine all the active filters in a group and combine them using ||
        activeFilter[filter.groupName] = filterString ? `( ${filterString} )` : "";
        return activeFilter[filter.groupName];
    }).filter(filter => filter);
    // Combine different group filters using &&
    return filtersList.join(" && ");
}

function parseFilter(filter){
    // generate filter String for active filters
    if(filter.isApplied){
        const tokens = filter.predicate.split(" ");
        const filterObjects = [];
        let filterObjectIndex = 0;
        let tokenIndex = 0;
        while(tokenIndex < tokens.length){
            if(operandTokens.includes(tokens[tokenIndex])){
                const filterObject = {
                    field: tokens[tokenIndex-1],
                    operand:tokens[tokenIndex],
                    value: tokens[tokenIndex+1],
                    fieldType: filter.filterType
                }
                // value has space in the value eg: "Van 48 LG"
                if(filterObject.value && filterObject.value.toString().startsWith("'")){
                    let remainingTokens = tokens.slice(tokenIndex + 1, tokens.length)
                    let endTokenIndex = remainingTokens.findIndex(token => token.toString().endsWith("'")); // finding ending token for value string
                    if(endTokenIndex > 0){
                        filterObject.value = remainingTokens.slice(0, endTokenIndex + 1).join(" "); // merge all strings between '
                        tokenIndex += endTokenIndex + 1;
                    }
                }
                filterObjects[filterObjectIndex-1] = getFilterOperation(filterObject);
                tokenIndex++;
            }else{
                filterObjects[filterObjectIndex]  = tokens[tokenIndex];
            }
            filterObjectIndex++;
            tokenIndex++;
        }
        return filterObjects.join(" ");
    } else{
        return "";
    }

}

// Generates a filter string for a single filter
function getFilterOperation({field, operand, value, fieldType}){
    let filterStatement = "";
    switch (operand) {
      case "equals":
        if (fieldType === "time") {
          filterStatement = `getTime(${field}) === getTime(${value})`;
        } else {
          filterStatement = `${field} === ${value}`;
        }
        break;
      case "includes":
        let arrayFieldData = `${field} && ${field}.map(data=>data.trim())`;
        filterStatement = `${arrayFieldData}.includes(${value})`;
        break;
      case "contains":
        // field has comma separated values in string eg: "Bobtail, Pup"
        // Eliminate extra whitespace(if any) from each element of array after split
        let fieldData = `${field} && ${field}.split(',').map(data=>data.trim())`;
        filterStatement = `${fieldData}.includes(${value})`;
        break;
      case "isOneOf":
        filterStatement = `${value} && ${value}.includes(${field})`;
        break;
      case "greaterThan":
        if (fieldType === "time") {
          filterStatement = `${field} && getTime(${field}) > getTime("${value}")`;
        } else {
          filterStatement = `${field} && ${field} > ${value}`;
        }
        break;
      case "greaterThanEqualTo":
        if (fieldType === "time") {
          filterStatement = `${field} && getTime(${field}) >= getTime("${value}")`;
        } else {
          filterStatement = `${field} && ${field} >= ${value}`;
        }
        break;
      case "lesserThan":
        if (fieldType === "time") {
          filterStatement = `${field} && getTime(${field}) < getTime("${value}")`;
        } else {
          filterStatement = `${field} && ${field} < ${value}`;
        }
        break;
      case "lesserThanEqualTo":
        if (fieldType === "time") {
          filterStatement = `${field} && getTime(${field}) <= getTime("${value}")`;
        } else {
          filterStatement = `${field} && ${field} <= ${value}`;
        }
        break;
      case "inBetween":
        let [minValue, maxValue] = value.split("-");
        if (fieldType === "time") {
          filterStatement = `${field} &&  getTime(${field}) >= getTime("${minValue}")  &&  getTime(${field}) <= getTime("${maxValue}") `;
        } else {
          filterStatement = `${field} && ${field} >= ${minValue} && ${field} <= ${maxValue} `;
        }
        break;
      case "dateRange":
        const [startDate, endDate] = value.split("/");
        const [fieldArray, fieldName] = field.split(".");
        filterStatement = `${fieldArray}?.some(option => {
          const rowDataDate = new Date(option.${fieldName});
          const startDateObj = new Date("${startDate}");
          const endDateObj = new Date("${endDate}");
          return rowDataDate >= startDateObj && rowDataDate <= endDateObj;
        })`;
        break;
      default:
        break;
    }
    return filterStatement ? `( ${filterStatement} )` : filterStatement;
}


// clears all filters by setting isApplied property of eahc individual filter to false
function clearFilter(filterObj){
    filterObj.filterGroups && Object.keys(filterObj.filterGroups).forEach(filterGroupKey => {
        const filterGroup = filterObj.filterGroups[filterGroupKey];
        filterGroup.filters.forEach(filter => {
            if(filter.actionType === "switch"){
                filter.options.forEach(f => f.isApplied = false);
            }else if(['daterange', 'select'].includes(filter.actionType)){
                const predicate = filter.predicate.split(" ");
                predicate[2]= "[]";
                filter.predicate =  predicate.join(" ");
                filter.isApplied = false;
            }
            else{
                filter.isApplied = false;
            }
        })
    });
    return filterObj;
}

// Do not remove this function , this function is used in getFilteredData when date type is time
const getTimeFromDate = (planDate) => {
    planDate = getUTCDate(planDate);
    return  (time) => {
        if(time){
            let dateTime = null;
            if(isTimeValid(time)){
                dateTime = parse(time, appConstant.DATETYPES.TIME.format, planDate);
            } else{
                dateTime = getUTCDate(time);
            }
            return differenceInMinutes(dateTime,planDate);

        }
        return null;
   }
};

// Used for filtering Data
// TODO: check for a better alternative to eval
const getFilteredData = (data, filterString, planDate) => {
    if(data.length > 0){
        let fields = Object.keys(data[0]).join(",");
        const getTime = getTimeFromDate(planDate); // don't remove this variable, it is called from the filerString below
        const filterFunction = eval(`(function filterData({${fields}}) { return ${filterString}})`);
        const filteredData = data.filter((element) => {
            return filterFunction(element);
        });
        return filteredData;
    }
    return data;
}

const iconList = {
   hazmat: {iconName: "icon-hazmat"},
   food: {iconName:"icon-food"},
   poison: {iconName:" icon-issue"},
   freezable: {iconName:"icon-freeze"},
   limited: {iconName:"icon-limited"},
   residential: {iconName:"icon-residential"},
   resuna: {iconName: "RESUNA", isSvgIcon: true},
   excessiveLength: {iconName: "ExcessiveLength", isSvgIcon: true},
   "2Man":{iconName:"2Man", isSvgIcon: true},
   CargoVan:{iconName:"CargoVan", isSvgIcon: true},
   liveUnload: {iconName: "LiveUnload", isSvgIcon: true},
   Hold: {iconName: "Hold", isSvgIcon: true},
   HoldRM: {iconName: "HoldRM", isSvgIcon: true},
   WhiteGloveDelivery: {iconName: "WhiteGloveDelivery", isSvgIcon: true},
   NINV: {iconName: "NINV", isSvgIcon: true},
   PhysicalDR: {iconName: "PhysicalDR", isSvgIcon: true},
}

export  { generateQuickFilterString, iconList, clearFilter, getFilteredData };